<template>
  <section
    class="my-8 mx-auto bg-black pt-6 px-2 md:px-10 pb-5 shadow-black shadow-xl divide-y-2 divide-white max-w-[1400px]"
  >
    <RallyLogoDownload link-text="Download Rally Navigator" />
    <div class="flex flex-col gap-4 mt-4">
      <h1 class="font-russo text-2xl md:text-3xl font-normal text-white mt-4">
        RALLY NAVIGATOR SUPPORTS OPENRALLY GPX FORMAT
      </h1>
      <h2 class="text-[#999999] font-helvetica text-[22px] font-semibold">
        Syncs to Leading Rally Monitoring & Digital Roadbook Systems
      </h2>
      <picture class="contents">
        <source srcset="/images/OpenRallyGPX-Screen-Shot.webp" type="image/webp" />
        <img
          decoding="async"
          fetchpriority="high"
          width="1919"
          height="1029"
          src="/images/OpenRallyGPX-Screen-Shot.png"
          class="attachment-full size-full wp-image-333"
          alt="OpenRally GPX export for rally timing and scoring in Rally Navigator"
        />
      </picture>
      <ul
        class="text-[#999999] text-[22px] font-helvetica font-semibold flex flex-col gap-4 mb-4"
      >
        <li>Create Rally Route - Add Rally Control Icons into Roadbook</li>
        <li>Export Perfectly Matched PDF Roadbook and GPX File</li>
        <li class="text-[17px]">
          Rally Navigator Route Data and Control Information is Exported in GPX
          Format. Support for Locations of Time Control, DSS, ASS, Stops, WPM,
          WPE, WPS, Checkpoints, DZ, FZ.
        </li>
        <li>
          <p class="text-[#FF0000] hover:underline">
            <a
              href="https://github.com/openrally/openrally/blob/cc-v1-DRAFT/cross-country/openrally.xsd"
            >
              Learn more about OpenRally Code and GPX Tags HERE
            </a>
          </p>
        </li>
      </ul>
    </div>
    <ul class="py-4 text-[#999999] font-helvetica font-semibold">
      <li>
        <h2 class="text-[22px]">
          Rally Navigator Export and Print Sample Files
        </h2>
      </li>
      <li class="text-[#999999] text-left">
        <div class="mt-12">
          <h2>RN2 File</h2>
          <ul class="ml-4 p-4 list-disc">
            <li>
              <p class="text-[#FF0000] hover:underline">
                <a
                  href="create-cross-country/Cross_Country_Rally_Sample.rn2"
                  download="Cross_Country_Rally_Sample.rn2"
                  target="_blank"
                >
                  OpenRally GPX Sample
                </a>
              </p>
            </li>
          </ul>
          <h2>OpenRally GPX Sample File</h2>
          <ul class="ml-4 p-4 list-disc">
            <li>
              <p class="text-[#FF0000] hover:underline">
                <a
                  href="create-cross-country/Cross_Country_Rally_Sample.gpx"
                  target="_blank"
                >
                  OpenRally GPX &#8211; Including OpenRally Scoring and Control
                  Data
                </a>
              </p>
            </li>
            <li>
              <p class="text-[#FF0000] hover:underline">
                <a
                  href="roadbooks/RN2 OpenRally GPX Cross Country Rally Sample Full Waypoint Data.gpx"
                >
                  OpenRally GPX &#8211; Full Waypoint Data &#8211; Can Render
                  Complete Roadbook
                </a>
              </p>
            </li>
          </ul>
          <h2>PDF File &#8211; Cross Country Roadbook</h2>
          <ul class="ml-4 p-4 list-disc">
            <li>
              <p class="text-[#FF0000] hover:underline">
                <a
                  href="roadbooks/RN2-OpenRally-GPX-Cross-Country-Rally-Sample-3.pdf"
                >
                  OpenRally GPX &#8211; Cross Country Sample Roadbook
                </a>
              </p>
            </li>
          </ul>
        </div>
      </li>
    </ul>
    <div class="py-8">
      <router-link to="/timing-scoring">
        <h2
          class="text-[#FF0000] font-russo font-black text-[22px] md:text-[26px]"
        >
          RALLY NAVIGATOR INTEGRATES WITH LEADING RALLY TRACKING SYSTEMS
        </h2>
      </router-link>
      <PartnersStandard :partner-link="true" />
      <ul
        class="font-helvetica font-semibold text-[#999999] flex flex-col gap-4 mb-4"
      >
        <li>
          Print PDF Roadbooks & Download Matching GPX File With Complete Stage
          Data - Loads Directly to Rally Tracking & Scoring System - Reduce
          Human Error & Save Time
        </li>
        <li>
          Contact Us to Integrate Your GPS Based Timing and Scoring Software
          with Rally Navigator
        </li>
        <li>
          <p class="text-[#FF0000] hover:underline">
            <a
              href="mailto:support@rallynavigator.com?subject=Inquiry%20about%20OpenRally%20GPX%20from%20Website"
            >
              support@rallynavigator.com
            </a>
          </p>
        </li>
      </ul>
    </div>
    <div class="py-8 flex flex-col gap-8">
      <h2 class="text-white text-[22px] md:text-[26px] font-russo font-black">
        RALLY NAVIGATOR OPENRALLY GPX CAN RENDER A COMPLETE ROADBOOK WITH SINGLE
        ENHANCED GPX FILE
      </h2>
      <picture class="contents">
        <source srcset="/images/Tablet-Portrait-qbqkj1crijaxx0syer5s1tmw07hdrwtp9crr0b1pe4.webp" type="image/webp" />
        <img width="450" height="750"
        decoding="async"
        src="/images/Tablet-Portrait-qbqkj1crijaxx0syer5s1tmw07hdrwtp9crr0b1pe4.png"
        title="Tablet-Portrait"
        alt="Tablet-Portrait"
        loading="lazy"
        class="max-w-[50%] m-auto"
        />
      </picture>
      <h2
        class="font-helvetica text-[20px] font-semibold text-[#A9A9A9] flex flex-col gap-4 mb-4"
      >
        Digital Roadbook Reader from TerraPirata Uses OpenRally GPX to Render
        this Roadbook (Not PDF). Also Includes Control Waypoints and Scoring
        Data in a Single GPX File.
      </h2>
    </div>
    <div class="py-8 m-auto w-full">
      <table class="m-auto border-gray-500 border-2">
        <thead>
          <tr>
            <th colspan="3" class="text-center pt-4">
              <h2 class="font-helvetica text-[22px] text-[#999999] mb-4">
                Sample GPX Extensions
              </h2>
            </th>
          </tr>
        </thead>
        <tbody class="border-gray-500 border-2 text-sm md:text-base">
          <tr class="border-gray-500 border-2">
            <td
              style="text-align: justify"
              class="border-gray-500 border-2 md:p-2"
            >
              <picture class="contents">
                <source srcset="/images/Start.webp" type="image/webp" />
                <img
                decoding="async"
                loading="lazy"
                class="alignnone size-full"
                src="/images/Start.png"
                alt="Rally timing start control icon"
                width="64"
                height="64"
                />
              </picture>
            </td>
            <td
              style="text-align: justify"
              class="border-gray-500 border-2 p-2"
            >
              <p class="px-2 py-1 bg-white">Start Special Stage</p>
            </td>
            <td
              style="text-align: justify"
              class="border-gray-500 border-2 p-2"
            >
              <p class="pr-8 pl-2 py-1 bg-white">&lt;openrally:dss/&gt;</p>
            </td>
          </tr>
          <tr class="border-gray-500 border-2">
            <td
              style="text-align: justify"
              class="border-gray-500 border-2 md:p-2"
            >
              <picture class="contents">
                <source srcset="/images/Finish.webp" type="image/webp" />
                <img
                decoding="async"
                loading="lazy"
                class="alignnone size-full"
                src="/images/Finish.png"
                alt="Rally timing finish control icon"
                width="64"
                height="64"
                />
              </picture>
            </td>
            <td
              style="text-align: justify"
              class="border-gray-500 border-2 p-2"
            >
              <p class="px-2 py-1 bg-white">Finish Special Stage</p>
            </td>
            <td
              style="text-align: justify"
              class="border-gray-500 border-2 p-2"
            >
              <p class="pr-8 pl-2 py-1 bg-white">&lt;openrally:ass/&gt;</p>
            </td>
          </tr>
          <tr class="border-gray-500 border-2">
            <td
              style="text-align: justify"
              class="border-gray-500 border-2 md:p-2"
            >
              <picture class="contents">
                <source srcset="/images/Stop.webp" type="image/webp" />
                <img
                decoding="async"
                loading="lazy"
                class="alignnone size-full"
                src="/images/Stop.png"
                alt="Rally timing stop control icon"
                width="64"
                height="64"
                />
              </picture>
            </td>
            <td
              style="text-align: justify"
              class="border-gray-500 border-2 p-2"
            >
              <p class="px-2 py-1 bg-white">Stop Control</p>
            </td>
            <td
              style="text-align: justify"
              class="border-gray-500 border-2 p-2"
            >
              <p class="pr-8 pl-2 py-1 bg-white">&lt;openrally:stop/&gt;</p>
            </td>
          </tr>
          <tr>
            <td
              style="text-align: justify"
              class="border-gray-500 border-2 md:p-2"
            >
              <picture class="contents">
                <source srcset="/images/E.webp" type="image/webp" />
                <img
                decoding="async"
                loading="lazy"
                class="alignnone size-full"
                src="/images/E.png"
                alt="Rally timing control point icon"
                width="64"
                height="64"
                />
              </picture>
            </td>
            <td
              style="text-align: justify"
              class="border-gray-500 border-2 p-2"
            >
              <p class="px-2 py-1 bg-white">Waypoint Eclipse</p>
            </td>
            <td
              style="text-align: justify"
              class="border-gray-500 border-2 p-2"
            >
              <p class="pr-8 pl-2 py-1 bg-white">&lt;openrally:wpe/&gt;</p>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
  <section class="my-8 max-w-[1400px] m-auto">
    <FeaturesBanner />
    <PartnersSmall />
    <RallyLogoDownload link-text="Download Rally Navigator" />
  </section>
</template>

<script>
import FeaturesBanner from './FeaturesBanner.vue';
import PartnersSmall from './PartnersSmall.vue';
import PartnersStandard from './PartnersStandard.vue';
import RallyLogoDownload from './RallyLogoDownload.vue';

export default {
  name: 'TimingScoring',
  components: {
    FeaturesBanner,
    RallyLogoDownload,
    PartnersStandard,
    PartnersSmall,
  },
};
</script>

<style scoped></style>
